import type { ChatReference } from '../interfaces/host';

/**
 * Actionable references. The agent emits standard markdown links using the
 * `archer-ref:` scheme; the Markdown renderer turns them into action chips that
 * post a structured {@link ChatReference} to the host instead of navigating.
 *
 * Accepted forms:
 *   archer-ref:order/12345
 *   archer-ref:shipment/SHIP-9?action=track
 *   archer-ref:order?id=12345&action=edit&warehouse=DC1
 *
 * Path is `<type>/<id>`; `action` defaults to "open"; any other query params are
 * passed through as `params`.
 */
const SCHEME = 'archer-ref:';

export function isReferenceHref(href: string | undefined | null): boolean {
  return typeof href === 'string' && href.toLowerCase().startsWith(SCHEME);
}

export function parseReference(href: string, label?: string): ChatReference | null {
  if (!isReferenceHref(href)) return null;

  const rest = href.slice(SCHEME.length);
  const [pathPart = '', queryPart = ''] = rest.split('?');

  const params: Record<string, string> = {};
  if (queryPart) {
    for (const [key, value] of new URLSearchParams(queryPart)) params[key] = value;
  }

  const segments = pathPart
    .split('/')
    .filter(Boolean)
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return s;
      }
    });

  const type = segments[0] ?? '';
  let id = segments[1] ?? '';
  if (!id && params.id) id = params.id;
  delete params.id;

  let action = 'open';
  if (params.action) {
    action = params.action;
    delete params.action;
  }

  if (!type || !id) return null;

  return {
    type,
    id,
    action,
    params: Object.keys(params).length > 0 ? params : undefined,
    label: label?.trim() || undefined,
  };
}
